// Hito 3 — Clases en Vivo (Sprint 1).
// Vista unificada para admin / franquiciado / docente / estudiante.
// Cada uno ve las clases que le competen y, según permisos, puede programar/editar/cancelar.
import { useEffect, useMemo, useState } from "react";
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  CalendarRange, Plus, Video, ExternalLink, Edit3, Trash2,
  Users, Folder, AlertCircle, Loader2, X, Radio, Clock, CheckCircle2, Mail, PhoneOff, Link2,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import HierarchyFilters, { EMPTY_HIER, hierParams } from "@/components/HierarchyFilters";

// ---------- Helpers (zona horaria fija: Ciudad de México) ----------
const MX_TZ = "America/Mexico_City";
const MX_OFFSET = "-06:00"; // CDMX no usa horario de verano desde 2022

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-MX", {
      timeZone: MX_TZ,
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function localInputValue(iso) {
  // datetime-local muestra "YYYY-MM-DDTHH:MM" en hora de CDMX.
  if (!iso) return "";
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: MX_TZ, year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    }).formatToParts(new Date(iso));
    const g = (t) => parts.find((p) => p.type === t)?.value;
    let hh = g("hour"); if (hh === "24") hh = "00";
    return `${g("year")}-${g("month")}-${g("day")}T${hh}:${g("minute")}`;
  } catch { return ""; }
}

function localInputToIso(value) {
  // El valor elegido se interpreta como hora de CDMX (offset fijo -06:00).
  if (!value) return "";
  return new Date(`${value}:00${MX_OFFSET}`).toISOString();
}

const STATUS_BADGES = {
  scheduled: { label: "Programada", cls: "bg-[#E8EEFB] text-[#2C49A0]", icon: CalendarRange },
  cancelled: { label: "Cancelada", cls: "bg-[#FFE9F0] text-[#A52258]", icon: X },
  completed: { label: "Completada", cls: "bg-[#E7F4E9] text-[#2A7A36]", icon: CheckCircle2 },
  expired: { label: "Finalizada", cls: "bg-[#EFF0F4] text-[#5A6178]", icon: Clock },
};

// ---------- Modal: Schedule / Edit ----------
function ScheduleModal({ open, onClose, onSaved, editing, teachers, students, currentUser }) {
  const isEdit = !!editing;
  const [form, setForm] = useState({
    title: "", description: "",
    scheduled_at: "",
    duration_min: 60,
    teacher_id: "",
    student_ids: [],
    timezone_str: "America/Mexico_City",
  });
  const [saving, setSaving] = useState(false);
  const [withGuestLink, setWithGuestLink] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        title: editing.title || "",
        description: editing.description || "",
        scheduled_at: localInputValue(editing.scheduled_at),
        duration_min: editing.duration_min || 60,
        teacher_id: editing.teacher_id,
        student_ids: editing.student_ids || [],
        timezone_str: editing.timezone || "America/Mexico_City",
      });
    } else {
      // Pre-select teacher if current user is teacher
      const defaultTeacher = currentUser?.role === "docente" ? currentUser.id : "";
      setForm({
        title: "", description: "",
        scheduled_at: localInputValue(new Date(Date.now() + 60 * 60 * 1000).toISOString()),
        duration_min: 60,
        teacher_id: defaultTeacher,
        student_ids: [],
        timezone_str: "America/Mexico_City",
      });
    }
  }, [open, editing, currentUser]);

  const [studentQ, setStudentQ] = useState("");
  const filteredStudents = useMemo(() => {
    if (!form.teacher_id) return students;
    const teacher = teachers.find((t) => t.id === form.teacher_id);
    if (!teacher) return students;
    // v5.6.5 · también los alumnos asignados a este docente (aunque su franquicia difiera) y orden A-Z
    const norm = (x) => (x || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    const q = norm(studentQ.trim());
    return students
      .filter((s) => s.franchise_id === teacher.franchise_id || s.teacher_id === teacher.id)
      .filter((s) => !q || norm(s.name).includes(q) || norm(s.email).includes(q))
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" }));
  }, [students, teachers, form.teacher_id, studentQ]);

  if (!open) return null;

  const toggleStudent = (id) => {
    setForm((f) => ({
      ...f,
      student_ids: f.student_ids.includes(id)
        ? f.student_ids.filter((x) => x !== id)
        : [...f.student_ids, id],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const title = form.title.trim();
    if (!title) return toast.error("El título es obligatorio");
    if (title.length < 3) return toast.error("El título debe tener al menos 3 caracteres");
    if (title.length > 80) return toast.error("El título no puede superar los 80 caracteres");
    if (!/[A-Za-zÁÉÍÓÚÜÑáéíóúüñ]/.test(title)) return toast.error("El nombre de la clase debe incluir letras, no solo números.");
    if (!form.scheduled_at) return toast.error("Define fecha y hora");
    if (!form.teacher_id) return toast.error("Selecciona un profesor");
    if (form.student_ids.length === 0) return toast.error("Selecciona al menos un estudiante");

    setSaving(true);
    try {
      const payload = {
        title,
        description: form.description.trim(),
        scheduled_at: localInputToIso(form.scheduled_at),
        duration_min: Number(form.duration_min),
        teacher_id: form.teacher_id,
        student_ids: form.student_ids,
        timezone_str: form.timezone_str,
      };
      if (isEdit) {
        await api.patch(`/live-classes/${editing.id}`, payload);
        toast.success("Clase actualizada");
      } else {
        const { data: created } = await api.post("/live-classes", payload);
        toast.success("Clase programada · invitaciones enviadas");
        if (withGuestLink && created?.id) {
          try {
            const { data: g } = await api.post(`/live-classes/${created.id}/guest-link`);
            try { await navigator.clipboard.writeText(g.guest_url); } catch (_) { /* sin permiso de portapapeles */ }
            toast.success("Link para invitados generado y copiado", { description: g.guest_url, duration: 12000 });
          } catch (_) {
            toast.error("La clase se creó, pero no se pudo generar el link de invitados. Genéralo desde la lista de clases.");
          }
        }
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="schedule-modal">
      <div className="absolute inset-0 bg-[#1F2547]/60 backdrop-blur-sm" onClick={onClose} />
      <form
        onSubmit={submit}
        className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-[#E1E5F0] max-h-[90vh] overflow-y-auto"
      >
        <div className="px-6 py-4 border-b border-[#E1E5F0] flex items-center justify-between sticky top-0 bg-white">
          <h3 className="font-display text-xl font-extrabold text-[#1F2547] inline-flex items-center gap-2">
            <Radio size={20} className="text-[#A43790]" />
            {isEdit ? "Editar clase en vivo" : "Programar clase en vivo"}
          </h3>
          <button type="button" onClick={onClose} className="text-[#6E7591] hover:text-[#1F2547]">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider font-extrabold text-[#6E7591]">Título</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              className="w-full mt-1 px-3 py-2 rounded-xl border-2 border-[#E1E5F0] focus:border-[#A43790] focus:outline-none text-sm"
              placeholder="Ej. Refuerzo de fracciones · 4o primaria"
              data-testid="schedule-title-input"
              maxLength={80}
              required
            />
            <p className="text-[11px] text-[#6E7591] mt-1 text-right" data-testid="schedule-title-counter">{form.title.length}/80</p>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider font-extrabold text-[#6E7591]">Descripción (opcional)</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={2}
              className="w-full mt-1 px-3 py-2 rounded-xl border-2 border-[#E1E5F0] focus:border-[#A43790] focus:outline-none text-sm"
              placeholder="Temas, objetivos, materiales…"
              data-testid="schedule-desc-input"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider font-extrabold text-[#6E7591]">Fecha y hora</label>
              <input
                type="datetime-local"
                value={form.scheduled_at}
                onChange={(e) => setForm((f) => ({ ...f, scheduled_at: e.target.value }))}
                className="w-full mt-1 px-3 py-2 rounded-xl border-2 border-[#E1E5F0] focus:border-[#A43790] focus:outline-none text-sm"
                data-testid="schedule-datetime-input"
                required
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-extrabold text-[#6E7591]">Duración (minutos)</label>
              <input
                type="number"
                min="10" max="240" step="5"
                value={form.duration_min}
                onChange={(e) => setForm((f) => ({ ...f, duration_min: e.target.value }))}
                className="w-full mt-1 px-3 py-2 rounded-xl border-2 border-[#E1E5F0] focus:border-[#A43790] focus:outline-none text-sm"
                data-testid="schedule-duration-input"
              />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider font-extrabold text-[#6E7591]">Profesor</label>
            <select
              value={form.teacher_id}
              onChange={(e) => setForm((f) => ({ ...f, teacher_id: e.target.value, student_ids: [] }))}
              className="w-full mt-1 px-3 py-2 rounded-xl border-2 border-[#E1E5F0] focus:border-[#A43790] focus:outline-none text-sm bg-white"
              data-testid="schedule-teacher-select"
              disabled={isEdit || currentUser?.role === "docente"}
              required
            >
              <option value="">— Selecciona —</option>
              {teachers.map((t) => (
                <option key={t.id} value={t.id}>{t.name} · {t.email}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider font-extrabold text-[#6E7591]">
              Estudiantes ({form.student_ids.length} seleccionado{form.student_ids.length === 1 ? "" : "s"})
            </label>
            <input className="jasq-input mt-1 !py-1.5 !text-xs" placeholder="Buscar estudiante por nombre o correo…"
              value={studentQ} onChange={(e) => setStudentQ(e.target.value)} data-testid="schedule-student-search" />
            <div className="mt-1 border-2 border-[#E1E5F0] rounded-xl max-h-48 overflow-y-auto p-2 bg-[#FAFBFE]">
              {filteredStudents.length === 0 ? (
                <p className="text-xs text-[#6E7591] py-2 text-center">
                  {form.teacher_id ? "No hay estudiantes en la franquicia del profesor seleccionado." : "Selecciona primero un profesor."}
                </p>
              ) : (
                filteredStudents.map((s) => {
                  const checked = form.student_ids.includes(s.id);
                  return (
                    <label
                      key={s.id}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer text-sm ${
                        checked ? "bg-[#A43790]/10 text-[#A43790] font-bold" : "hover:bg-white text-[#1F2547]"
                      }`}
                      data-testid={`schedule-student-${s.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleStudent(s.id)}
                        className="accent-[#A43790]"
                      />
                      <span className="flex-1 truncate">{s.name}</span>
                      <span className="text-[10px] text-[#6E7591]">{s.email}</span>
                    </label>
                  );
                })
              )}
            </div>
            <p className="text-[10px] text-[#6E7591] mt-1">Tip: marca uno solo para una clase 1-a-1, varios para una clase grupal.</p>
          </div>
        </div>

        {!isEdit && (
          <div className="px-6 pb-3">
            <label className="flex items-center gap-2 text-sm text-[#1F2547] cursor-pointer">
              <input type="checkbox" checked={withGuestLink} onChange={(e) => setWithGuestLink(e.target.checked)}
                className="accent-[#A43790]" data-testid="schedule-guest-link-check" />
              <Link2 size={14} className="text-[#A43790]" /> Generar también el link para invitados externos
            </label>
          </div>
        )}

        <div className="px-6 py-4 border-t border-[#E1E5F0] flex items-center justify-end gap-2 sticky bottom-0 bg-white">
          <button type="button" onClick={onClose} className="jasq-btn-ghost text-sm" data-testid="schedule-cancel-btn">
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            className="jasq-btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-60"
            data-testid="schedule-submit-btn"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <CalendarRange size={14} />}
            {saving ? "Guardando…" : isEdit ? "Guardar cambios" : "Programar y enviar invitaciones"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------- Aislante por tarjeta ----------
// Si UNA clase llega con datos incompletos/corruptos, evitamos que su error de
// render tumbe toda la página (y dispare el KidErrorBoundary global). En su
// lugar, esa tarjeta simplemente no se muestra y las demás siguen visibles.
class CardBoundary extends React.Component {
  constructor(props) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(error) { console.warn("Clase con datos incompletos, se omite su tarjeta:", error); }
  render() { return this.state.failed ? null : this.props.children; }
}

// ---------- Class card ----------
function ClassCard({ cls, currentUser, onEdit, onCancel, onDelete, onTestReminder, onEnd, onGuestLink }) {
  const navigate = useNavigate();
  const location = useLocation();
  // Guard: si la clase llega vacía/incompleta, no renderizamos (no crash).
  if (!cls || !cls.id) return null;
  // Detectar prefijo de ruta para construir la URL de la sala dentro del rol actual.
  const rolePrefix = (() => {
    const p = (location.pathname || "/").split("/")[1];
    return ["admin", "franquiciado", "docente", "estudiante"].includes(p) ? `/${p}` : "/admin";
  })();
  const status = STATUS_BADGES[cls.status] || STATUS_BADGES.scheduled;
  const StatusIcon = status.icon;
  const canManage =
    currentUser?.is_super_admin ||
    currentUser?.role === "admin" ||
    (currentUser?.role === "franquiciado" && cls.franchise_id === currentUser?.franchise_id) ||
    cls.created_by === currentUser?.id;
  const isStudent = currentUser?.role === "estudiante";
  const startMs = new Date(cls.scheduled_at).getTime();
  const endMs = startMs + (cls.duration_min || 60) * 60000;
  const nowMs = Date.now();
  const windowOpen = nowMs >= startMs - 10 * 60000 && nowMs <= endMs;
  const hasRoom = !!(cls.agora_channel || cls.jitsi_room_alias || cls.meet_link);
  // Estudiante: solo 10 min antes → fin. Docente/gestores: mientras no termine.
  // Se permite reingresar a clases 'completed' (p. ej. tras guardar una
  // grabación intermedia) siempre que sigan dentro de su ventana y NO hayan
  // sido finalizadas por el docente (Bug 4). 'cancelled'/'expired' no reingresan.
  const rejoinable = (cls.status === "scheduled" || cls.status === "completed") && !cls.live_session_ended;
  const canJoinNow = rejoinable && hasRoom && (isStudent ? windowOpen : nowMs <= endMs);
  const studentWaiting = isStudent && cls.status === "scheduled" && hasRoom && !windowOpen && nowMs < startMs;

  // Finalizar clase en curso: gestores y el docente asignado.
  const canEnd =
    (canManage || (currentUser?.role === "docente" && cls.teacher_id === currentUser?.id)) &&
    hasRoom && !cls.live_session_ended &&
    (cls.status === "scheduled" || cls.status === "completed") &&
    nowMs >= startMs - 10 * 60000;

  // Link de invitados: gestores y docente asignado, mientras la clase no termine.
  const canGuestLink =
    (canManage || (currentUser?.role === "docente" && cls.teacher_id === currentUser?.id)) &&
    !cls.live_session_ended && (cls.status === "scheduled" || cls.status === "completed");

  return (
    <div className="jasq-card p-4 hover:shadow-lg transition-shadow" data-testid={`live-class-card-${cls.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display font-extrabold text-[#1F2547] text-lg break-words min-w-0">{cls.title}</h3>
            {cls.franchise_name && (currentUser?.role === "admin" || currentUser?.role === "franquiciado") && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#EEF1FB] text-[#2C49A0]" data-testid="class-franchise-tag">{cls.franchise_name}</span>
            )}
            <span className={`jasq-pill text-xs ${status.cls}`}><StatusIcon size={11} /> {status.label}</span>
            {cls.is_one_to_one && <span className="jasq-pill bg-[#FFF7DA] text-[#9C7A18] text-xs">1-a-1</span>}
          </div>
          {cls.description && <p className="text-xs text-[#6E7591] mt-1 line-clamp-2">{cls.description}</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 mt-3 text-xs">
            <div className="flex items-center gap-1.5 text-[#4C5478]">
              <Clock size={12} />
              <span className="font-bold">{formatDateTime(cls.scheduled_at)}</span>
              <span className="text-[#6E7591]">· {cls.duration_min} min</span>
            </div>
            <div className="flex items-center gap-1.5 text-[#4C5478]">
              <Users size={12} />
              <span className="font-bold">{cls.teacher_name}</span>
              <span className="text-[#6E7591]">+ {(cls.student_ids || []).length} alumno{cls.student_ids?.length === 1 ? "" : "s"}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2 pt-3 border-t border-[#E1E5F0]">
        {canJoinNow && (
          <button
            onClick={() => navigate(`${rolePrefix}/clases-en-vivo/${cls.id}/sala`)}
            data-testid={`live-class-join-${cls.id}`}
            className="jasq-btn-primary inline-flex items-center gap-2 text-xs"
          >
            <Video size={13} /> Unirse a la clase
          </button>
        )}
        {studentWaiting && (
          <span
            data-testid={`live-class-wait-${cls.id}`}
            className="inline-flex items-center gap-1.5 text-xs font-bold text-[#9C7A18] bg-[#FFF7DA] rounded-full px-3 py-1.5"
          >
            <Clock size={12} /> Disponible 10 min antes del inicio
          </span>
        )}
        {cls.calendar_link && (
          <a
            href={cls.calendar_link}
            target="_blank"
            rel="noreferrer"
            className="jasq-btn-ghost inline-flex items-center gap-2 text-xs"
          >
            <CalendarRange size={13} /> Calendar <ExternalLink size={10} />
          </a>
        )}
        {cls.drive_folder_link && (
          <a
            href={cls.drive_folder_link}
            target="_blank"
            rel="noreferrer"
            className="jasq-btn-ghost inline-flex items-center gap-2 text-xs"
          >
            <Folder size={13} /> Drive <ExternalLink size={10} />
          </a>
        )}
        {canGuestLink && (
          <button
            type="button"
            onClick={() => onGuestLink(cls)}
            data-testid={`live-class-guest-link-${cls.id}`}
            title="Copiar el link para invitados externos de esta clase"
            className="text-xs font-bold text-[#A43790] inline-flex items-center gap-1 hover:underline"
          >
            <Link2 size={12} /> Link invitados
          </button>
        )}
        {canEnd && (
          <button
            type="button"
            onClick={() => onEnd(cls)}
            data-testid={`live-class-end-${cls.id}`}
            title="Finalizar la clase para todos"
            className="text-xs font-extrabold px-3 py-1.5 rounded-full bg-[#A52258] text-white hover:bg-[#8A1C49] inline-flex items-center gap-1"
          >
            <PhoneOff size={12} /> Finalizar clase
          </button>
        )}
        {canManage && cls.status !== "cancelled" && (
          <>
            <button
              type="button"
              onClick={() => onTestReminder(cls)}
              data-testid={`live-class-test-reminder-${cls.id}`}
              title="Enviar un email de recordatorio de PRUEBA al docente y a los estudiantes"
              className="ml-auto text-xs font-bold text-[#9C7A18] inline-flex items-center gap-1 hover:underline"
            >
              <Mail size={12} /> Probar recordatorio
            </button>
            <button
              type="button"
              onClick={() => onEdit(cls)}
              data-testid={`live-class-edit-${cls.id}`}
              className="text-xs font-bold text-[#2C49A0] inline-flex items-center gap-1 hover:underline"
            >
              <Edit3 size={12} /> Editar
            </button>
            <button
              type="button"
              onClick={() => onCancel(cls)}
              data-testid={`live-class-cancel-${cls.id}`}
              className="text-xs font-bold text-[#A52258] inline-flex items-center gap-1 hover:underline"
            >
              <X size={12} /> Cancelar
            </button>
          </>
        )}
        {canManage && cls.status === "cancelled" && (
          <button
            type="button"
            onClick={() => onDelete(cls)}
            data-testid={`live-class-delete-${cls.id}`}
            className="ml-auto text-xs font-bold text-[#A52258] inline-flex items-center gap-1 hover:underline"
          >
            <Trash2 size={12} /> Eliminar
          </button>
        )}
      </div>
    </div>
  );
}

// ---------- Main page ----------
export default function LiveClasses() {
  const { user } = useAuth();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("upcoming");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [googleStatus, setGoogleStatus] = useState(null);
  const [videoProvider, setVideoProvider] = useState(null);
  const [hier, setHier] = useState(EMPTY_HIER); // v5.6.5 · filtros franquicia/institución/colegio/salón

  const canSchedule = user?.is_super_admin || ["admin", "franquiciado", "docente"].includes(user?.role);

  const refresh = async () => {
    setLoading(true);
    try {
      const params = { ...hierParams(hier) };
      if (filter === "upcoming") params.upcoming = true;
      else if (filter !== "all") params.status = filter;
      const { data } = await api.get("/live-classes", { params });
      // Guard defensivo: si el backend devolviera algo que no es un arreglo
      // (p. ej. un objeto de error con 200), no reventamos el .map del render.
      setClasses(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudieron cargar las clases");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filter, hier]);
  useEffect(() => {
    api.get("/live-classes/_/provider").then((r) => setVideoProvider(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!canSchedule) return;
    (async () => {
      try {
        const [tRes, sRes, gRes] = await Promise.allSettled([
          api.get("/users", { params: { role: "docente" } }),
          api.get("/users", { params: { role: "estudiante" } }),
          user?.is_super_admin ? api.get("/google/status") : Promise.resolve({ data: null }),
        ]);
        if (tRes.status === "fulfilled") setTeachers(tRes.value.data.filter((t) => t.status !== "suspended"));
        if (sRes.status === "fulfilled") setStudents(sRes.value.data.filter((s) => s.status !== "suspended"));
        if (gRes.status === "fulfilled" && gRes.value.data) setGoogleStatus(gRes.value.data);
      } catch (_) {}
    })();
  }, [canSchedule, user?.is_super_admin]);

  const handleSchedule = () => { setEditing(null); setModalOpen(true); };
  const handleEdit = (cls) => { setEditing(cls); setModalOpen(true); };
  const handleCancel = async (cls) => {
    if (!window.confirm(`¿Cancelar "${cls.title}"? Se eliminará el evento de Google Calendar y se notificará a los participantes.`)) return;
    try {
      await api.patch(`/live-classes/${cls.id}`, { status: "cancelled" });
      toast.success("Clase cancelada");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    }
  };
  const handleGuestLink = async (cls) => {
    try {
      let { data } = await api.get(`/live-classes/${cls.id}/guest-link`);
      if (!data?.guest_url) ({ data } = await api.post(`/live-classes/${cls.id}/guest-link`));
      try { await navigator.clipboard.writeText(data.guest_url); } catch (_) { /* sin permiso de portapapeles */ }
      toast.success("Link de invitados copiado", { description: data.guest_url, duration: 10000 });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo generar el link");
    }
  };
  const handleEnd = async (cls) => {
    if (!window.confirm(`¿Finalizar "${cls.title}" para todos? Se desconectará a todos los participantes y se retirará del agendamiento.`)) return;
    try {
      await api.post(`/live-classes/${cls.id}/end`);
      toast.success("Clase finalizada para todos.");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo finalizar.");
    }
  };
  const handleDelete = async (cls) => {
    if (!window.confirm(`¿Eliminar definitivamente "${cls.title}"?`)) return;
    try {
      await api.delete(`/live-classes/${cls.id}`);
      toast.success("Clase eliminada");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    }
  };
  const handleTestReminder = async (cls) => {
    try {
      const { data } = await api.post(`/live-classes/${cls.id}/test-reminder`);
      const n = data?.sent || 0;
      toast.success(`Recordatorio de prueba enviado a ${n} destinatario(s): ${(data?.recipients || []).join(", ")}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    }
  };

  const filteredCount = Array.isArray(classes) ? classes.length : 0;
  const needsGoogle = canSchedule && googleStatus && !googleStatus.connected;
  const isStudent = user?.role === "estudiante";

  // v5.5.0 · «Próximas»: la más cercana primero; el resto: la más reciente primero.
  const sortedClasses = useMemo(() => {
    const list = (Array.isArray(classes) ? classes : []).filter((c) => c && c.id);
    const ts = (c) => { const t = Date.parse(c.scheduled_at || c.created_at || ""); return Number.isNaN(t) ? 0 : t; };
    return [...list].sort((a, b) => (filter === "upcoming" ? ts(a) - ts(b) : ts(b) - ts(a)));
  }, [classes, filter]);

  return (
    <div className="space-y-6" data-testid="live-classes-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#A43790]">Hito 3</p>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#1F2547] inline-flex items-center gap-3">
            <Radio size={30} className="text-[#A43790]" />
            Clases en vivo
          </h1>
          <p className="text-sm text-[#6E7591] mt-1">
            {isStudent
              ? "Tus clases en vivo: entra a la sala cuando sea la hora. Las invitaciones se envían por email automáticamente."
              : <>Programación, sala de videoconferencia <b>Agora.io</b> y carpetas Drive para tus clases en vivo. Las invitaciones se envían por email automáticamente.</>}
          </p>
          <p className="text-xs text-[#A43790] font-bold mt-1 inline-flex items-center gap-1">
            <Mail size={11} /> El sistema envía recordatorios automáticos por email 24 h y 2 h antes de cada clase, y un aviso al iniciar.
          </p>
        </div>
        {canSchedule && (
          <button
            type="button"
            onClick={handleSchedule}
            data-testid="schedule-class-btn"
            className="jasq-btn-primary inline-flex items-center gap-2"
          >
            <Plus size={16} /> Programar clase
          </button>
        )}
      </div>

      {videoProvider && !isStudent && user?.is_super_admin && !videoProvider.is_configured && (
        <div
          className={`jasq-card p-3 border-2 flex items-start gap-3 ${videoProvider.is_configured ? "border-[#3FA84D] bg-[#E8F5E9]/40" : "border-[#F5B82E] bg-[#FFF7DA]/40"}`}
          data-testid="video-provider-banner"
        >
          <Radio size={18} className={videoProvider.is_configured ? "text-[#1B5E20]" : "text-[#9C7A18]"} />
          <div className="text-sm">
            <p className="font-extrabold text-[#1F2547]">
              Plataforma de video: <span data-testid="video-provider-label">{videoProvider.provider}</span>
            </p>
            <p className="text-xs text-[#4C5478] mt-0.5">
              {videoProvider.is_configured
                ? `✓ ${videoProvider.provider} configurado · Hasta 100+ sesiones simultáneas con autenticación segura.${videoProvider.cloud_recording ? " Cloud Recording → Backblaze B2 activo." : ""}`
                : "⚠ Motor de video no configurado — pide al Super Admin que configure las claves de Agora.io en producción."}
            </p>
          </div>
        </div>
      )}

      {needsGoogle && user?.is_super_admin && (
        <div className="jasq-card p-4 border-2 border-[#F5B82E] bg-[#FFF7DA]/40 flex items-start gap-3" data-testid="google-warning">
          <AlertCircle size={20} className="text-[#9C7A18] shrink-0" />
          <div className="text-sm">
            <p className="font-extrabold text-[#1F2547]">Google Workspace no está conectado</p>
            <p className="text-xs text-[#4C5478] mt-1">
              Conecta la cuenta operativa central desde{" "}
              <a href="/admin/configuracion" className="text-[#A43790] font-bold underline">Configuración del Sistema</a>{" "}
              para que el sistema pueda crear enlaces de Google Meet y carpetas en Drive.
            </p>
          </div>
        </div>
      )}

      {(user?.role === "admin" || user?.role === "franquiciado") && (
        <HierarchyFilters value={hier} onChange={setHier} />
      )}

      {/* Filtros */}
      <div className="flex flex-wrap gap-2" data-testid="live-classes-filters">
        {[
          { id: "upcoming", label: "Próximas" },
          { id: "all", label: "Todas" },
          { id: "scheduled", label: "Programadas" },
          { id: "completed", label: "Completadas" },
          { id: "expired", label: "Finalizadas" },
          { id: "cancelled", label: "Canceladas" },
        ].map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            data-testid={`filter-${f.id}`}
            className={`text-xs font-bold px-3 py-1.5 rounded-full border-2 transition-colors ${
              filter === f.id ? "border-[#A43790] bg-[#A43790] text-white" : "border-[#E1E5F0] text-[#4C5478] hover:border-[#A43790] hover:text-[#A43790]"
            }`}
          >
            {f.label}
          </button>
        ))}
        <span className="ml-auto text-xs text-[#6E7591] self-center" data-testid="live-classes-count">
          {loading ? "Cargando…" : `${filteredCount} clase${filteredCount === 1 ? "" : "s"}`}
        </span>
      </div>

      {/* Lista */}
      {loading ? (
        <div className="text-center py-12 text-[#6E7591]">
          <Loader2 size={20} className="animate-spin inline mr-2" /> Cargando clases…
        </div>
      ) : filteredCount === 0 ? (
        <div className="jasq-card p-10 text-center" data-testid="live-classes-empty">
          <CalendarRange size={42} className="mx-auto text-[#A6ACC1] mb-3" />
          <p className="font-display text-lg font-extrabold text-[#1F2547]">Nada por aquí</p>
          <p className="text-sm text-[#6E7591] mt-1">
            {canSchedule
              ? "Aún no has programado clases en vivo. Crea tu primera clase con el botón superior."
              : "Aún no tienes clases en vivo programadas."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="live-classes-grid">
          {sortedClasses
            .map((cls) => (
              <CardBoundary key={cls.id}>
                <ClassCard
                  cls={cls}
                  currentUser={user}
                  onEdit={handleEdit}
                  onCancel={handleCancel}
                  onDelete={handleDelete}
                  onEnd={handleEnd}
                  onGuestLink={handleGuestLink}
                  onTestReminder={handleTestReminder}
                />
              </CardBoundary>
            ))}
        </div>
      )}

      <ScheduleModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={refresh}
        editing={editing}
        teachers={teachers}
        students={students}
        currentUser={user}
      />
    </div>
  );
}
